import { getDate } from "~/common/utils/ApiHelper";
import { occFetch, occUrl } from "~/app/api/occ/occFetch";
import { transformProduct } from "~/features/productDetails/transformers/ProductDataTransformer";
import type { ContractDetailsResponse, ContractLine } from "~/features/contracts/models/models";
import type { ContractDetailsDTO, ContractLineDTO } from "./dto";
import { transformContractOverviewDTO, transformContractPrice } from "./getContractsOverview";

async function fetchContract(
  contractNo: string,
  materialNo: string,
  packCode: string,
): Promise<ContractDetailsResponse> {
  const url = occUrl(`/contract/${contractNo}/${materialNo}/${packCode}`);
  const response = await occFetch(url);

  if (!response.ok) {
    throw new Error(`Error fetching contract details! Code ${response.status} - ${response.statusText}`);
  }

  const dto = (await response.json()) as ContractDetailsDTO;
  if (!dto.product) {
    throw new Error("Contract details response did not contain a product");
  }

  return {
    contractLines: (dto.contractLines ?? []).map(transformContractLineDTO),
    lineNumber: dto.lineNumber ?? 0,
    scheduleLineNumber: dto.scheduleLineNumber ?? 0,
    product: transformProduct(dto.product),
    openQuantity: dto.openQuantity ?? 0,
    quantityRequested: dto.quantityRequested,
    packSize: dto.packSize,
    packCode: dto.packCode ?? "",
    nettoPrice: transformContractPrice(dto.nettoPrice),
    totalPrice: transformContractPrice(dto.totalPrice),
    materialNumber: dto.materialNumber ?? "",
    materialDescription: dto.materialDescription ?? "",
    customerMaterial: dto.customerMaterial ?? "",
    priceUnit: dto.priceUnit ?? "",
    nextDisposition: toDate(dto.nextDisposition),
    nextDispositionQuantity: dto.nextDispositionQuantity ?? 0,
    contractValidFrom: toDate(dto.contractValidFrom),
    contractValidTo: toDate(dto.contractValidTo),
    datePlaced: toDate(dto.datePlaced),
    customerContractNumber: dto.customerContractNumber ?? "",
    shipToAddress: dto.shipToAddress ?? undefined,
    schaefflerContractNumber: dto.schaefflerContractNumber ?? "",
    orderType: dto.orderType ?? "",
    comment: dto.comment ?? "",
    contractPredecessor: dto.contractPredecessor ? transformContractOverviewDTO(dto.contractPredecessor) : undefined,
    status: dto.status ?? "",
  };
}

function transformContractLineDTO(dto: ContractLineDTO): ContractLine {
  return {
    scheduleLineNum: dto.scheduleLineNum ?? 0,
    salesOrderLine: dto.salesOrderLine ?? undefined,
    orderNumber: dto.orderNumber ?? "",
    orderQuantity: dto.orderQuantity ?? 0,
    openQuantity: dto.openQuantity ?? 0,
    disposition: toDate(dto.disposition),
  };
}

function toDate(value?: string | null): Date | undefined {
  return value ? getDate(value) : undefined;
}

export function getContractDetails(
  contractNo: string,
  materialNo: string,
  packCode: string,
  _contractPosition: number | null,
): Promise<ContractDetailsResponse> {
  return fetchContract(contractNo, materialNo, packCode);
}
